import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProducts } from '@/lib/api';
import ProductCard from '@/components/ProductCard';
import SectionHeader from '@/components/SectionHeader';

export default function MostOrderedSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    data: products,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['products', 'most-ordered'],
    queryFn: async () => {
      const all = await getProducts();
      return all.filter((product) => product.category !== 'bundles');
    },
  });

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.75;
    el.scrollBy({ left: dir === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  return (
    <section className="border-t border-rp-line bg-rp-paper py-10 md:py-20">
      <div className="rp-container">
        <SectionHeader eyebrow="Most ordered" title="Reference-grade research peptides" />

        {isLoading && (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="animate-spin text-rp-forest" size={32} />
          </div>
        )}

        {isError && (
          <div className="flex h-40 items-center justify-center rounded-2xl border border-rp-line bg-rp-bone">
            <p className="text-sm text-rp-moss">
              Unable to load products. Please try again later.
            </p>
          </div>
        )}

        {products && products.length > 0 && (
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="relative mt-6 md:mt-8"
          >
            <div
              ref={scrollRef}
              className="flex cursor-grab select-none snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:-mx-8 lg:px-8"
            >
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="w-[82%] flex-shrink-0 snap-start sm:w-[45%] md:w-[32%] lg:w-[24%]"
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>

            <button
              type="button"
              aria-label="Scroll to previous product"
              onClick={() => scroll('left')}
              className="absolute left-2 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-rp-line bg-rp-paper text-rp-forest shadow-dropdown lg:flex"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Scroll to next product"
              onClick={() => scroll('right')}
              className="absolute right-2 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-rp-line bg-rp-paper text-rp-forest shadow-dropdown lg:flex"
            >
              <ChevronRight size={18} />
            </button>
          </motion.div>
        )}

        {products && products.length === 0 && (
          <div className="flex h-40 items-center justify-center rounded-2xl border border-rp-line bg-rp-bone">
            <p className="text-sm text-rp-moss">No products available right now.</p>
          </div>
        )}
      </div>
    </section>
  );
}
